import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, assetUrl } from '../api/index.js';
import { useAuth } from '../contexts/AuthContext.jsx';

function HeartIcon({ filled }) {
  return (
    <svg
      className={`w-5 h-5 transition-colors ${filled ? 'text-red-500 fill-red-500' : 'text-gray-400'}`}
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={filled ? 0 : 2}
        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
      />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  );
}

export default function PostCard({ post: initialPost, onDelete }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [post, setPost] = useState(initialPost);
  const [liking, setLiking] = useState(false);

  async function handleLike(e) {
    e.preventDefault();
    if (!user) { navigate('/signin'); return; }
    if (liking) return;
    setLiking(true);
    // Optimistic update
    const wasLiked = post.liked;
    setPost((p) => ({
      ...p,
      liked: !p.liked,
      like_count: p.like_count + (wasLiked ? -1 : 1),
    }));
    try {
      await api.posts.like(post.id);
    } catch {
      // Revert on error
      setPost((p) => ({
        ...p,
        liked: wasLiked,
        like_count: p.like_count + (wasLiked ? 1 : -1),
      }));
    } finally {
      setLiking(false);
    }
  }

  function handleDM(e) {
    e.preventDefault();
    if (!user) { navigate('/signin'); return; }
    navigate(`/messages/${post.user_id}`);
  }

  const isOwner = user?.sub === post.user_id || user?.id === post.user_id;

  return (
    <div className="card overflow-hidden group">
      {/* Image */}
      <Link to={`/posts/${post.id}`}>
        <div className="aspect-square overflow-hidden bg-gray-100">
          <img
            src={assetUrl(post.image_url)}
            alt={post.caption || 'Golf post'}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        </div>
      </Link>

      {/* Footer */}
      <div className="p-3">
        {/* Author row */}
        <div className="flex items-center gap-2 mb-2">
          <Link to={`/caddies/${post.user_id}`} className="flex items-center gap-2 flex-1 min-w-0">
            {post.avatar_url ? (
              <img src={assetUrl(post.avatar_url)} alt={post.display_name} className="w-7 h-7 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 text-xs font-bold shrink-0">
                {post.display_name?.[0]?.toUpperCase()}
              </div>
            )}
            <span className="text-sm font-medium text-gray-800 truncate">{post.display_name}</span>
          </Link>
        </div>

        {/* Caption */}
        {post.caption && (
          <p className="text-sm text-gray-600 mb-2 line-clamp-2">{post.caption}</p>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleLike}
            className="flex items-center gap-1 text-sm hover:opacity-80 transition-opacity"
            aria-label={post.liked ? 'Unlike' : 'Like'}
          >
            <HeartIcon filled={!!post.liked} />
            <span className={post.liked ? 'text-red-500' : 'text-gray-500'}>{post.like_count}</span>
          </button>

          <button
            onClick={handleDM}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-brand-700 transition-colors"
            aria-label="Send DM"
          >
            <ChatIcon />
            <span className="hidden sm:inline text-xs">DM</span>
          </button>

          {isOwner && onDelete && (
            <button
              onClick={() => onDelete(post.id)}
              className="ml-auto text-xs text-gray-400 hover:text-red-500 transition-colors"
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
